import React, { useState } from 'react';
import Button from '../common/Button';
import './loginStyle.css';

// A "noauth" from the NVR only tells us the session is no longer valid — NOT the exact cause.
// So rather than assert one cause as fact ("someone logged in from a different device"), the
// base modal stays SHORT ("you've been signed out, sign in again") and tucks the nitty-gritty
// behind an info (ⓘ) toggle. `reason` ({ cause, method, url }) carries the request that tripped
// the expiry (set by axiosSessionInterceptor).
//
// `showSupportInfo` gates the support-facing detail (what-was-happening + a Support reference
// token). It defaults to OFF because most session-expiries are BENIGN (a routine timeout, or a
// legitimate sign-in elsewhere) — surfacing "contact support" on those just generates false-alarm
// calls. A caller that knows the end was genuinely anomalous opts in. Raw internals (member IPs /
// full URLs) never reach the customer modal — they stay in the console log. (#86bb7phhh)

// Map the tripping RPC to a plain-language operation the user would recognize.
const OP_LABELS = {
  'event.getEventsByTime': 'loading the recorded-video timeline',
  'event.getEventPresence': 'loading the recorded-video timeline',
  'event.setEventFlag': 'updating a recording',
  'config.user.getSets': 'loading your views',
  'config.server.getCameras': 'loading cameras',
  'config.user.getRights': 'loading your permissions',
  'config.user.getCameras': 'loading your camera access',
};

export const describeSessionEnd = (reason) => {
  if (!reason || !reason.method || reason.method === 'unknown') return null;
  const op = OP_LABELS[reason.method];
  const where = op ? `while ${op}` : 'during a background request';
  return { where, ref: reason.method };
};

const SessionExpiredModal = ({ onAccept, eviewMode = false, reason = null, showSupportInfo = false }) => {
  const modalClass = `login-modal ${eviewMode ? 'eview-mode' : ''}`;
  const [showWhy, setShowWhy] = useState(false);
  const detail = describeSessionEnd(reason);
  // The session ended because no device is left to this account (AuthActions.applyRefreshedCloudAccess):
  // signing in again would not help, so say what happened instead.
  if (reason && reason.cause === 'access_removed') {
    return (
      <div className={modalClass}>
        <p className="login-modal__title">Access Removed</p>
        <p className="login-modal__text">Your account no longer has access to any device.</p>
        <div className="login-modal__buttons">
          <Button onClick={onAccept} buttonText="OK" />
        </div>
      </div>
    );
  }

  return (
    <div className={modalClass}>
      <p className="login-modal__title">Session Expired</p>
      <p className="login-modal__text">
        You&apos;ve been signed out. Please sign in again.
        <button
          type="button"
          className="login-modal__why"
          aria-label="Why did this happen?"
          aria-expanded={showWhy}
          title="Why did this happen?"
          onClick={() => setShowWhy((v) => !v)}
        >&#9432;</button>
      </p>

      {showWhy && (
        <div className="login-modal__why-detail">
          <p className="login-modal__text login-modal__text--detail">
            This usually happens when the account signs in on another device, or the session
            times out on the server.
          </p>
          {showSupportInfo && detail && (
            <p className="login-modal__text login-modal__text--detail">
              It ended {detail.where}.<br />
              <span className="login-modal__ref">Support reference: {detail.ref}</span>
            </p>
          )}
        </div>
      )}

      <div className="login-modal__buttons">
        <Button onClick={onAccept} buttonText="OK" />
      </div>
    </div>
  );
};

export default SessionExpiredModal;